import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  MapPin,
  Bell,
  Compass,
  Share2,
  Settings2,
  Clock,
  ArrowLeft,
  Mail,
} from "lucide-react";
import { CONTACT_EMAIL } from "../lib/constants";

const Section = ({ icon: Icon, title, children, testId }) => (
  <section
    data-testid={testId}
    className="rounded-3xl bg-white border border-[#7c3aed]/12 p-7 sm:p-8 shadow-[0_10px_30px_-15px_rgba(124,58,237,0.2)]"
  >
    <div className="flex items-center gap-4 mb-4">
      <div className="w-12 h-12 rounded-2xl icon-bubble flex items-center justify-center text-white shrink-0">
        <Icon size={22} strokeWidth={2.2} />
      </div>
      <h2 className="font-display font-bold text-xl sm:text-2xl text-[#2e1065]">
        {title}
      </h2>
    </div>
    <div className="text-[#3c1e6b]/80 text-[15px] sm:text-base leading-relaxed space-y-3">
      {children}
    </div>
  </section>
);

const Bullet = ({ children }) => (
  <li className="flex items-start gap-2.5">
    <span className="mt-2 shrink-0 w-1.5 h-1.5 rounded-full bg-[#7c3aed]" />
    <span>{children}</span>
  </li>
);

export default function PrivacyPolicy() {
  const updatedAt = "Junho de 2026";

  return (
    <div data-testid="privacy-page">
      {/* HERO */}
      <section className="relative overflow-hidden brand-bg">
        <div className="absolute inset-0 paw-pattern opacity-60 pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8 pt-14 sm:pt-20 pb-10 sm:pb-14">
          <Link
            to="/"
            data-testid="privacy-back-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#6d28d9] hover:text-[#5B21B6] mb-6"
          >
            <ArrowLeft size={16} /> Voltar para o início
          </Link>

          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/80 border border-[#7c3aed]/15 text-xs sm:text-sm font-semibold text-[#6d28d9] shadow-sm">
            <ShieldCheck size={14} className="text-[#14B8A6]" />
            Documento oficial
          </span>

          <h1
            className="mt-5 font-display font-bold text-4xl sm:text-5xl leading-[1.05] text-[#2e1065]"
            data-testid="privacy-title"
          >
            Política de <span className="brand-gradient-text">Privacidade</span>
          </h1>
          <p className="mt-4 text-[#3c1e6b]/70 text-base">
            Última atualização: {updatedAt}
          </p>
          <p className="mt-4 text-[#3c1e6b]/80 max-w-2xl leading-relaxed">
            Sua privacidade importa. Aqui explicamos, de forma clara, como o
            aplicativo <strong>Pets · Perdidos e Achados</strong> utiliza sua
            localização e cuida dos seus dados.
          </p>
        </div>
      </section>

      {/* CONTENT */}
      <section className="relative py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 space-y-6">
          <Section icon={MapPin} title="Uso da localização" testId="privacy-section-usage">
            <p>
              O aplicativo Pets Perdidos e Achados utiliza a localização do
              dispositivo para oferecer sua principal funcionalidade: avisar
              os usuários quando estiverem próximos de um animal perdido ou
              encontrado.
            </p>
            <p>
              A localização é utilizada somente com a autorização do usuário.
            </p>
            <p>O aplicativo pode acessar a localização:</p>
            <ul className="space-y-2 pl-1">
              <Bullet>enquanto estiver em uso; e</Bullet>
              <Bullet>
                em segundo plano, quando o usuário conceder a permissão
                <em> “Permitir o tempo todo”</em>.
              </Bullet>
            </ul>
          </Section>

          <Section
            icon={Bell}
            title="Finalidade da localização em segundo plano"
            testId="privacy-section-background"
          >
            <p>
              O acesso à localização em segundo plano é indispensável para o
              funcionamento da funcionalidade principal do aplicativo.
            </p>
            <p>
              Quando autorizado, o aplicativo monitora de forma eficiente a
              aproximação do usuário em relação aos locais onde existem
              registros ativos de animais perdidos ou encontrados.
            </p>
            <p>
              Sempre que o usuário entrar em um raio aproximado de 5
              quilômetros de uma ocorrência cadastrada, o aplicativo poderá
              enviar uma notificação informando sobre o animal, permitindo que
              ele possa identificá-lo durante seu deslocamento.
            </p>
            <p>
              Essa funcionalidade aumenta significativamente as chances de
              localização e devolução dos animais aos seus tutores.
            </p>
            <p>
              Sem essa permissão, o aplicativo continuará permitindo consultas
              manuais no mapa, porém não conseguirá enviar alertas automáticos
              quando o usuário estiver se deslocando com o aplicativo fechado.
            </p>
          </Section>

          <Section
            icon={Compass}
            title="Como utilizamos a localização"
            testId="privacy-section-how"
          >
            <p>A localização é utilizada exclusivamente para:</p>
            <ul className="space-y-2 pl-1">
              <Bullet>
                identificar a aproximação do usuário de ocorrências
                cadastradas;
              </Bullet>
              <Bullet>
                calcular a distância entre o usuário e animais perdidos ou
                encontrados;
              </Bullet>
              <Bullet>enviar notificações automáticas de proximidade;</Bullet>
              <Bullet>
                melhorar a eficiência do processo de localização dos animais.
              </Bullet>
            </ul>
          </Section>

          <Section icon={Share2} title="Compartilhamento" testId="privacy-section-share">
            <ul className="space-y-2 pl-1">
              <Bullet>Os dados de localização não são vendidos.</Bullet>
              <Bullet>Eles não são utilizados para publicidade.</Bullet>
              <Bullet>
                Não são compartilhados com terceiros para fins comerciais.
              </Bullet>
              <Bullet>
                São utilizados exclusivamente para fornecer os serviços do
                aplicativo.
              </Bullet>
            </ul>
          </Section>

          <Section
            icon={Settings2}
            title="Controle da permissão"
            testId="privacy-section-control"
          >
            <p>O usuário pode:</p>
            <ul className="space-y-2 pl-1">
              <Bullet>conceder ou negar a permissão;</Bullet>
              <Bullet>
                alterar a permissão a qualquer momento nas configurações do
                Android;
              </Bullet>
              <Bullet>desativar completamente os alertas por localização.</Bullet>
            </ul>
          </Section>

          <Section icon={Clock} title="Retenção" testId="privacy-section-retention">
            <p>
              As informações de localização são utilizadas apenas durante o
              tempo necessário para detectar a proximidade entre o usuário e
              ocorrências cadastradas.
            </p>
            <p>
              Não mantemos histórico permanente da localização do usuário além
              do necessário para o funcionamento do serviço.
            </p>
          </Section>

          {/* Contact */}
          <div
            className="rounded-3xl p-8 sm:p-10 border border-[#7c3aed]/12 bg-gradient-to-br from-white via-[#f5edff] to-white shadow-[0_10px_30px_-15px_rgba(124,58,237,0.25)]"
            data-testid="privacy-contact-block"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl icon-bubble-teal flex items-center justify-center text-white shrink-0">
                <Mail size={22} strokeWidth={2.2} />
              </div>
              <div>
                <h3 className="font-display font-bold text-xl text-[#2e1065]">
                  Dúvidas sobre privacidade?
                </h3>
                <p className="mt-2 text-[#3c1e6b]/80 leading-relaxed">
                  Se você tiver qualquer dúvida sobre esta política ou sobre
                  como seus dados são tratados, entre em contato conosco pelo
                  e-mail abaixo. Respondemos o mais rápido possível.
                </p>
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  data-testid="privacy-contact-email"
                  className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#6d28d9] hover:text-[#5B21B6] break-all"
                >
                  <Mail size={16} className="text-[#14B8A6]" />
                  {CONTACT_EMAIL}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
